import type { IMediaRepository } from "@/core/repositories/interfaces/IMediaRepository";
import type { IMediaService } from "@/core/services/interfaces/IMediaService";
import {
  ARTICLE_COVER_IMAGE_MAX_BYTES,
  TAXONOMY_ICON_MAX_BYTES,
  isAcceptedCoverImageType,
  isAcceptedTaxonomyIconType,
} from "@/core/services/types/media.types";

export class MediaService implements IMediaService {
  constructor(private readonly repository: IMediaRepository) {}

  async uploadArticleCoverImage(articleId: string, file: File) {
    if (!articleId.trim()) {
      return { success: false as const, error: "Missing article id" };
    }

    if (!(file instanceof File) || file.size === 0) {
      return { success: false as const, error: "Missing image file" };
    }

    if (!isAcceptedCoverImageType(file.type)) {
      return { success: false as const, error: "Unsupported image type" };
    }

    if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
      return { success: false as const, error: "Image file is too large" };
    }

    return this.repository.uploadArticleCoverImage(articleId, file);
  }

  async uploadEventCoverImage(eventId: string, file: File) {
    if (!eventId.trim()) {
      return { success: false as const, error: "Missing event id" };
    }

    if (!(file instanceof File) || file.size === 0) {
      return { success: false as const, error: "Missing image file" };
    }

    if (!isAcceptedCoverImageType(file.type)) {
      return { success: false as const, error: "Unsupported image type" };
    }

    if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
      return { success: false as const, error: "Image file is too large" };
    }

    return this.repository.uploadEventCoverImage(eventId, file);
  }

  async uploadPlaceCoverImage(placeId: string, file: File) {
    if (!placeId.trim()) {
      return { success: false as const, error: "Missing place id" };
    }

    if (!(file instanceof File) || file.size === 0) {
      return { success: false as const, error: "Missing image file" };
    }

    if (!isAcceptedCoverImageType(file.type)) {
      return { success: false as const, error: "Unsupported image type" };
    }

    if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
      return { success: false as const, error: "Image file is too large" };
    }

    return this.repository.uploadPlaceCoverImage(placeId, file);
  }

  async uploadTaxonomyIcon(file: File) {
    if (!(file instanceof File) || file.size === 0) {
      return { success: false as const, error: "Missing icon file" };
    }

    const looksLikeSvg =
      isAcceptedTaxonomyIconType(file.type) ||
      file.name.toLowerCase().endsWith(".svg");

    if (!looksLikeSvg) {
      return { success: false as const, error: "Unsupported icon type" };
    }

    if (file.size > TAXONOMY_ICON_MAX_BYTES) {
      return { success: false as const, error: "Icon file is too large" };
    }

    const normalized =
      file.type === "image/svg+xml"
        ? file
        : new File([file], file.name, { type: "image/svg+xml" });

    return this.repository.uploadTaxonomyIcon(normalized);
  }
}
